/**
 * NIGHTBOUND - species configuration.
 *
 * All starter data is data-driven so that new species (and later, mutated
 * hybrids) can be added without touching components or the simulation.
 */

import type { WildKey } from './future/wild'

export const SPECIES_IDS = ['DOG', 'CAT', 'BIRD', 'SNAKE'] as const
export type SpeciesId = (typeof SPECIES_IDS)[number]

/** How the creature prefers to sleep. Future traits (e.g. NOCTURNAL) override. */
export type SleepPreference = 'NOCTURNAL' | 'CREPUSCULAR' | 'VARIABLE'

export type ActivityState =
  | 'IDLE'
  | 'SLEEPING'
  | 'EATING'
  | 'PLAYING'
  | 'CLEANING'
  | 'TRAINING'
  | 'HURT'

export type MoodId =
  | 'HAPPY'
  | 'CONTENT'
  | 'HUNGRY'
  | 'DIRTY'
  | 'TIRED'
  | 'LONELY'
  | 'UNWELL'
  | 'SLEEPING'

export type StatKey = 'hp' | 'attack' | 'defence' | 'speed'

/**
 * Per-species multipliers. Everything the simulation needs is declarative -
 * the engine never special-cases a species with an `if`.
 */
export interface SpeciesModifiers {
  hungerDecay: number
  hygieneDecay: number
  /** Multiplier on energy recovery while asleep (sleep profile). */
  sleepRecovery: number
  bondGain: number
}

export interface SpeciesDefinition {
  id: SpeciesId
  name: string
  family: 'Canine' | 'Feline' | 'Avian' | 'Reptile'
  role: string
  temperament: string
  /** Shown on the starter selection card. */
  shortDescription: string
  /** Longer flavour text shown under the preview. */
  description: string
  /** A line the creature "says" while being considered. */
  whisper: string
  baseStats: {
    hp: number
    attack: number
    defence: number
    speed: number
  }
  startingAbilityKey: string
  sleep: {
    preference: SleepPreference
    label: string
    /** Hours [start, end) in the player's local time that the creature wants to sleep. */
    sleepWindow: readonly [number, number]
    /** Hours [start, end) where a crepuscular animal is most active. */
    activeWindows: readonly (readonly [number, number])[]
  }
  modifiers: SpeciesModifiers
  /** Art/theme hooks. `assetKey` also names the asset folder. */
  assetKey: string
  /** Accent colour used for the creature's UI tints (matches the reference palette). */
  accent: string
  /** Small glyph used in compact/mobile UI. */
  glyph: string
  /**
   * Future mutation affinity - used once Essence and mutations arrive.
   * Kept here so the data model does not need reshaping later.
   */
  mutationAffinity: readonly WildKey[]
}

export const SPECIES: Record<SpeciesId, SpeciesDefinition> = {
  DOG: {
    id: 'DOG',
    name: 'Dog',
    family: 'Canine',
    role: 'Balanced',
    temperament: 'Loyal',
    shortDescription: 'Steady, devoted, hard to break.',
    description:
      'A scrapper from the cold streets above. It trusts you completely, which is exactly what will make this difficult. Balanced in every regard and eager to please.',
    whisper: 'It presses its head into your palm and waits.',
    baseStats: { hp: 110, attack: 12, defence: 11, speed: 10 },
    startingAbilityKey: 'bite',
    sleep: {
      preference: 'NOCTURNAL',
      label: 'Night',
      sleepWindow: [22, 7],
      activeWindows: [[7, 22]],
    },
    modifiers: {
      hungerDecay: 1,
      hygieneDecay: 1,
      sleepRecovery: 1,
      bondGain: 1.15,
    },
    assetKey: 'dog',
    accent: '#c9a46a',
    glyph: '🐕',
    mutationAffinity: ['WOLF'],
  },
  CAT: {
    id: 'CAT',
    name: 'Cat',
    family: 'Feline',
    role: 'Fast Attacker',
    temperament: 'Independent',
    shortDescription: 'Quick, sharp, and self-possessed.',
    description:
      'It followed you down the stairs once and never explained why. Fast and precise, though it decides for itself when affection is permitted. Keeps itself clean.',
    whisper: 'It blinks slowly at you. That is either trust or calculation.',
    baseStats: { hp: 90, attack: 13, defence: 8, speed: 15 },
    startingAbilityKey: 'claw',
    sleep: {
      preference: 'CREPUSCULAR',
      label: 'Crepuscular',
      sleepWindow: [10, 16],
      activeWindows: [
        [5, 9],
        [18, 23],
      ],
    },
    modifiers: {
      hungerDecay: 1,
      hygieneDecay: 0.8,
      sleepRecovery: 1.05,
      bondGain: 0.9,
    },
    assetKey: 'cat',
    accent: '#8f9bb3',
    glyph: '🐈',
    mutationAffinity: ['LYNX'],
  },
  BIRD: {
    id: 'BIRD',
    name: 'Bird',
    family: 'Avian',
    role: 'Speed / Evasion',
    temperament: 'Alert',
    shortDescription: 'Darting, watchful, always ready to leave.',
    description:
      'Something small that survived the dark by never being still. Fastest of the four, fragile, and constantly scanning the room for exits.',
    whisper: 'Its head flicks toward every sound you make.',
    baseStats: { hp: 80, attack: 10, defence: 7, speed: 18 },
    startingAbilityKey: 'peck',
    sleep: {
      preference: 'NOCTURNAL',
      label: 'Night',
      sleepWindow: [21, 6],
      activeWindows: [[6, 21]],
    },
    modifiers: {
      hungerDecay: 1.2,
      hygieneDecay: 1.05,
      sleepRecovery: 0.95,
      bondGain: 0.95,
    },
    assetKey: 'bird',
    accent: '#a8a08a',
    glyph: '🐦',
    mutationAffinity: ['CROW'],
  },
  SNAKE: {
    id: 'SNAKE',
    name: 'Snake',
    family: 'Reptile',
    role: 'Status / Ambush',
    temperament: 'Cold',
    shortDescription: 'Patient, unmoving, waiting for the right moment.',
    description:
      'It has been down here longer than you have. Slow to trust, slower to act, and it needs far less than the others. When it finally moves, it will matter.',
    whisper: 'It watches without blinking. You are not sure it has ever stopped.',
    baseStats: { hp: 95, attack: 11, defence: 9, speed: 12 },
    startingAbilityKey: 'fang',
    sleep: {
      preference: 'VARIABLE',
      label: 'Variable',
      sleepWindow: [0, 24],
      activeWindows: [],
    },
    modifiers: {
      hungerDecay: 0.7,
      hygieneDecay: 0.7,
      sleepRecovery: 0.9,
      bondGain: 0.85,
    },
    assetKey: 'snake',
    accent: '#7e8f74',
    glyph: '🐍',
    mutationAffinity: ['VIPER'],
  },
}

export const SPECIES_LIST: SpeciesDefinition[] = SPECIES_IDS.map((id) => SPECIES[id])

export function isSpeciesId(value: unknown): value is SpeciesId {
  return typeof value === 'string' && (SPECIES_IDS as readonly string[]).includes(value)
}

export function getSpecies(id: SpeciesId): SpeciesDefinition {
  return SPECIES[id]
}

/** Local clock hour (0-23.x) for a UTC instant, given a timezone offset. */
export function localHourOf(date: Date, timezoneOffsetMinutes: number): number {
  const localMs = date.getTime() + timezoneOffsetMinutes * 60_000
  const hoursIntoDay = ((localMs / 3_600_000) % 24 + 24) % 24
  return hoursIntoDay
}

function hourInWindow(hour: number, window: readonly [number, number]): boolean {
  const [start, end] = window
  if (start === 0 && end >= 24) return true
  if (start <= end) return hour >= start && hour < end
  // Window wraps past midnight.
  return hour >= start || hour < end
}

/**
 * Is this a time the creature wants to be asleep?
 * Data-driven via the species sleep profile - no hard-coded species checks.
 */
export function isPreferredSleepTime(
  species: SpeciesDefinition,
  date: Date,
  timezoneOffsetMinutes: number,
): boolean {
  if (species.sleep.preference === 'VARIABLE') return false
  const hour = localHourOf(date, timezoneOffsetMinutes)
  return hourInWindow(hour, species.sleep.sleepWindow)
}

/**
 * Is this a time the creature is naturally restless / unable to settle?
 * Used for sleep recovery quality and window flavour text.
 */
export function isNaturalActiveTime(
  species: SpeciesDefinition,
  date: Date,
  timezoneOffsetMinutes: number,
): boolean {
  const hour = localHourOf(date, timezoneOffsetMinutes)
  if (species.sleep.preference === 'VARIABLE') {
    // Snakes are sluggish outside the warmer hours.
    return hour >= 8 && hour < 22
  }
  if (species.sleep.activeWindows.length === 0) return true
  return species.sleep.activeWindows.some((w) => hourInWindow(hour, w))
}

export function sleepWindowLabel(species: SpeciesDefinition): string {
  const [start, end] = species.sleep.sleepWindow
  const fmt = (h: number) => `${String(h % 24).padStart(2, '0')}:00`
  if (species.sleep.preference === 'VARIABLE') return 'No fixed cycle'
  return `${fmt(start)} - ${fmt(end)}`
}
